import { useState } from "react";
import {
  SetterOrUpdater,
  useRecoilState,
  useResetRecoilState,
  useSetRecoilState,
} from "recoil";
import { useHistory, useLocation } from "react-router-dom";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  AllstarUser,
  AllstarNovu,
  AllstarSearchSuggestionsPreview,
} from "../../State";
import { SuggestedUsersState } from "../../State/suggestedUsers";
import { User, IAuthenticatedUserQuery } from "../../Model/User";
import { FTUE_STATE } from "../../GraphQL/ftue";
import { IAllstarUser } from "../../Views/Setup/Components/@types";
import {
  FTUEGameState,
  FTUEProcessingState,
  FTUEProfileState,
  FTUEState,
  TFlowState,
} from "../../State/ftue";
import { SignatureSelectorState } from "../../State/loadoutSelector";
import { LedgerState } from "../../State/ledger";
import { GET_ME, LOGIN, SIGNUP } from "../../GraphQL/user";
import { MY_BALANCE } from "../../GraphQL/montage";
import { URLS } from "../../Utilities/Maps";
import { noticeError } from "../../Utilities/NewRelic";
import { analytics } from "MVVM/Analytics";
import { useMutateFollowers } from "MVVM/Hooks/useMutateFollowers";

const BASE_URL: string = `${process.env.REACT_APP_API_URL}`;

export const useUser = (): {
  allstarUser: IAllstarUser; // Record<string, any>;
  setAllstarUser: SetterOrUpdater<any>;
  loading: boolean;
  getFreshProfile: (
    token?: string,
    forceFtueCheck?: boolean,
  ) => Promise<boolean | void>;
  login: (email: string, password: string) => Promise<any>;
  signup: (
    email: string,
    password: string,
    isMobileApp: boolean,
  ) => Promise<any>;

  logout: (logoutReason: string) => void;
  error: string | null;
} => {
  const location = useLocation();
  const history = useHistory();
  // const [ftueRecoilState, setFtueRecoilState] = useRecoilState(FTUEState);
  const userModel = User.getInstance();
  const [allstarUser, setAllstarUser] = useRecoilState(AllstarUser);
  const [ftueRecoilState, setFtueRecoilState] = useRecoilState(FTUEState);
  const [ftueGameState, setFtueRecoilGameState] = useRecoilState(FTUEGameState);
  const [isProcessingFtue, setIsProcessingFtue] =
    useRecoilState(FTUEProcessingState);

  const resetFtueGameState = useResetRecoilState(FTUEGameState);
  const resetFtueState = useResetRecoilState(FTUEState);
  const resetFtueProfileState = useResetRecoilState(FTUEProfileState);
  const resetFtueProcessingState = useResetRecoilState(FTUEProcessingState);
  const resetSignatureState = useResetRecoilState(SignatureSelectorState);
  const resetUserState = useResetRecoilState(AllstarUser);
  const resetAllstarNovu = useResetRecoilState(AllstarNovu);
  const resetSuggestedUsers = useResetRecoilState(SuggestedUsersState);
  const setSuggestedUsers = useSetRecoilState(SuggestedUsersState);
  const resetAllstarSearchSuggestionsPreview = useResetRecoilState(
    AllstarSearchSuggestionsPreview,
  );
  const setLedgerState = useSetRecoilState(LedgerState);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [ftueState] = useLazyQuery(FTUE_STATE, { fetchPolicy: "network-only" });
  const [getMe] = useLazyQuery<IAuthenticatedUserQuery>(GET_ME, {
    fetchPolicy: "network-only",
  });
  const [getBalanceUpdate] = useLazyQuery(MY_BALANCE, {
    fetchPolicy: "network-only",
  });
  const [loginGQL] = useLazyQuery(LOGIN, {
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });
  const [signupGQL] = useMutation(SIGNUP);
  const { requestFollowers } = useMutateFollowers();

  async function getFreshProfile(
    token?: string | null,
    forceFtueCheck?: boolean,
  ) {
    setLoading(true);
    if (token) localStorage.setItem("token", token);
    else token = localStorage.getItem("token");
    try {
      const decoded = await userModel.DecodeToken();
      if (!decoded) {
        logout("MALFORMED TOKEN");
        return;
      }

      const isEscalated = !typeof decoded.escalated === undefined;
      const authenticatedUserQuery = await getMe();
      const userProfile = authenticatedUserQuery?.data?.me;
      // If the request failed:
      // token has expired.

      if (!userProfile) {
        logout("INVALID TOKEN");
        return;
        // return logout();
      }
      let createdDate = new Date(userProfile.user.createdDate);
      if (createdDate.toString() === "Invalid Date") {
        const numberDate = Number(userProfile.user.createdDate);
        createdDate = new Date(numberDate);
      }

      const stateData = {
        _id: userProfile.user._id,
        admin: userProfile.user.admin,
        avatar: userProfile.user.avatar,
        commentsDisabled: userProfile.user.commentsDisabled,
        credits: userProfile.user.credits,
        discord: userProfile.user.discord,
        discordSignUp: userProfile.user.discordSignUp,
        faceit: userProfile.user.faceit,
        followingCount: userProfile.user.followingCount,
        google: userProfile.user.google,
        hasClips: false,
        hasSharecodes: userProfile.metadata.hasSharecodes,
        hasUsername: userProfile.profile?.username ? true : false,
        noEmailSet: userProfile.metadata.noEmailSet,
        profile: userProfile.profile,
        rank: userProfile.user.rank,
        riot: userProfile.user.riot,
        steam: userProfile.user.steam,
        tiktok: userProfile.user.tiktok,
        epic: userProfile.user.epic,
        battlenet: userProfile.user.battlenet,
        username: userProfile.user.username,
        usernameLastChanged: userProfile.user?.usernameLastChanged,
        userGroup: userProfile.user.userGroup,
        userScopes: userProfile.user.scope,
        stats: userProfile.stats,
        createdDate: createdDate,
      };

      window.rudderanalytics.identify(decoded?._id, {
        email: decoded.sub,
        userGroup: stateData?.userGroup?.type,
        username: stateData?.profile?.username || stateData?.username,
      });

      const followersResult = await requestFollowers({
        variables: { profiles: true, clips: false },
      });

      const followers = followersResult?.data?.myFollowing?.profiles;

      const checkFtueState =
        location.pathname.includes("/register") ||
        location.pathname.includes("/login") ||
        forceFtueCheck;
      const next = localStorage.getItem("next");

      // Feature gate new FTUE until 100% rollout
      const useNewFtue = process.env.REACT_APP_USE_NEW_FTUE === "true";
      if (useNewFtue && checkFtueState) setIsProcessingFtue(true);

      setAllstarUser({
        escalated: isEscalated,
        loggedIn: true,
        isAdmin: decoded.admin,
        token,
        user: stateData,
        decoded: decoded,
        BASE_URL,
        myAvatar: stateData.avatar,
        followers: followers,
      });

      if (useNewFtue && checkFtueState) {
        try {
          const { data: ftue } = (await ftueState()) as {
            data: {
              ftue: {
                hasCompleted: boolean;
              };
            };
          };

          if (ftue?.ftue?.hasCompleted === true) {
            setIsProcessingFtue(false);
            setFtueRecoilState({
              ...ftueRecoilState,
              stateLoaded: true,
              ftueComplete: true,
            });
            const next = localStorage.getItem("next");
            localStorage.removeItem("next");
            return history.push(next || URLS.PROFILE);
          }

          if (!location.pathname.includes("welcome")) {
            return history.replace("/welcome");
          }
          return;
        } catch (error) {
          setIsProcessingFtue(false);
          console.log("Error from ftueState query: ", error);
        }
      }

      if (checkFtueState) {
        try {
          setIsProcessingFtue(true);

          const { data: ftue } = (await ftueState()) as {
            data: {
              ftue: {
                hasCompleted: boolean;
                step: TFlowState;
                stepHistory: TFlowState[];
                gameSelection: string[];
              };
            };
          };

          if (ftue.ftue.hasCompleted === false) {
            setFtueRecoilState({
              ...ftueRecoilState,
              step: ftue.ftue.step ?? "profile",
              stepHistory: ftue.ftue.stepHistory ?? [],
              stateLoaded: true,
            });

            setFtueRecoilGameState((prevState) => {
              return {
                ...prevState,
                gameSelection: prevState.gameSelection.map((game) => {
                  return {
                    ...game,
                    selected: ftue?.ftue?.gameSelection?.includes(game.id),
                  };
                }),
              };
            });

            if (!location.pathname.includes("setup")) {
              return history.push("/setup");
            }
          } else {
            setIsProcessingFtue(false);
          }
          localStorage.removeItem("next");
          if (next) {
            history.push(next);
            return;
          }
          return history.push(`${URLS.PROFILE}`);
        } catch (error) {
          setIsProcessingFtue(false);
        }
      }
      localStorage.removeItem("next");
      if (next) {
        return history.push(next);
      }

      try {
        const { data } = await getBalanceUpdate({
          context: { headers: { authorization: `Bearer ${token}` } },
        });
        if (data?.myLedgerBalance?.balance !== undefined) {
          setLedgerState({ localBalance: data.myLedgerBalance.balance });
        }
      } catch (balanceError) {
        analytics.track("Error Loading Ledger Balance on Profile Fetch", {
          msg: "Generic error",
          error: balanceError,
        });
      }

      setLoading(false);

      return true;
    } catch (error: any) {
      setError(error);
      setLoading(false);
    }
  }

  const login = async (email: string, password: string) => {
    const data = await loginGQL({
      variables: {
        email,
        password,
      },
    });

    const token = data?.data?.authentication;

    if (token) {
      document.cookie = `token=${token}; SameSite=None; Secure; path=/`;
      localStorage.setItem("token", token);
      analytics.track("Login", {
        loginType: "email",
      });
      analytics.track("Login - New User Redirected", {});
      const decoded = await userModel.DecodeToken();
      if (!decoded?._id) noticeError("No user ID found in token oauth login");

      await getFreshProfile();

      const next = localStorage.getItem("next");
      if (next) {
        localStorage.removeItem("next");
        history.push(next);
      }
      return { status: "SUCCESS" };
    }

    if (data.error)
      return { status: "FAILED", message: "Invalid email or password." };

    return { status: "FAILED", message: "Something went wrong." };
  };

  const signup = async (email: string, password: string) => {
    const signupData = await signupGQL({
      variables: {
        email,
        password,
      },
    });

    if (signupData?.data?.signup?.errors?.length) {
      return { status: "FAILED", message: "Email already exists." };
    }

    const token = signupData?.data?.signup?.token;
    localStorage.setItem("token", token);
    document.cookie = `token=${token}; SameSite=None; Secure; path=/`;
    if (token) {
      analytics.track("Signup", {
        type: "email",
      });
      const decoded = await userModel.DecodeToken();
      if (!decoded?._id) noticeError("No user ID found in token oauth signup");

      await getFreshProfile(token);

      return { status: "SUCCESS", message: "" };
    }

    return { status: "FAILED", message: "Something went wrong." };
  };

  const EXCLUDED_NEXT_PATHS = ["/404", "/reset/form"];
  const PATHS_REQUIRE_AUTH = [
    "/support",
    "/subscriptions",
    "/connectedaccounts",
    "/accountsettings",
    "/notifications",
    "/dashboard",
  ];

  function logout(logoutReason: string) {
    localStorage.removeItem("payload");
    localStorage.removeItem("token");
    document.cookie = "token=; SameSite=None; Secure; path=/";
    resetFtueGameState();
    resetFtueState();
    resetFtueProfileState();
    resetFtueProcessingState();
    resetSignatureState();
    resetUserState();
    resetAllstarNovu();
    resetAllstarSearchSuggestionsPreview();
    setSuggestedUsers((prevState) => {
      const newState = { ...prevState };
      Object.keys(newState).forEach((cacheKey) => {
        if (cacheKey.startsWith("logged-in")) {
          delete newState[cacheKey];
        }
      });
      return newState;
    });

    analytics.track("Logout", {
      logoutReason,
      id: allstarUser?.user?._id,
    });

    if (!PATHS_REQUIRE_AUTH.includes(window.location.pathname)) return;
    if (EXCLUDED_NEXT_PATHS.includes(window.location.pathname))
      window.location.href = "/login";
    return (window.location.href = `/login?next=${window.location.pathname}`);
  }

  return {
    allstarUser,
    setAllstarUser,
    getFreshProfile,
    loading,
    error,
    login,
    signup,
    logout,
  };
};
